import { useState, useEffect } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card } from "../components/Shared";
import { toast } from "sonner";
import { Plugs, Copy, ArrowsClockwise, MagnifyingGlass, StackSimple } from "@phosphor-icons/react";

export default function OdooTest() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [fields, setFields] = useState(null);
  const [fieldQuery, setFieldQuery] = useState("");
  const [stages, setStages] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    const { data } = await api.get("/odoo/settings");
    setSettings(data);
    setForm({
      odoo_url: data.odoo_url || "", odoo_db: data.odoo_db || "",
      odoo_username: data.odoo_username || "", odoo_password: "",
      odoo_perdido_field: data.odoo_perdido_field || "perdido",
      odoo_pipeline_slug: data.odoo_pipeline_slug || "",
      preview_mode: !!data.preview_mode,
    });
    const t = await api.get("/odoo/event-token");
    setTokenInfo(t.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.odoo_password) delete payload.odoo_password; // no sobreescribir con vacío
      await api.put("/odoo/settings", payload);
      toast.success("Configuración guardada");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const run = async (label, fn) => {
    setBusy(label);
    try { const { data } = await fn(); 
      if (data.success === false) toast.error(data.error || "Falló");
      else toast.success(data.preview ? `${label} (PREVIEW OK)` : `${label} OK`);
      return data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(""); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copiado"); };

  if (!settings) return <div className="p-10 text-carbon-500">Cargando…</div>;

  return (
    <>
      <PageHeader label="Sistema · Integración" title="Test Odoo CRM"
        description="Configura la conexión bidireccional con Odoo, prueba la API y obtén los webhooks para las automatizaciones de Odoo." />
      <PageBody>
        {/* Configuración */}
        <Card className="p-5 mb-4">
          <div className="section-label mb-3">Conexión Odoo</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {[
              ["odoo_url", "URL Odoo", "https://miempresa.odoo.com"],
              ["odoo_db", "Base de datos", "miempresa"],
              ["odoo_username", "Usuario", "admin@empresa.com"],
              ["odoo_password", "API Key / Password", settings.odoo_password_set ? "•••••• (configurada)" : "pega tu API key"],
              ["odoo_pipeline_slug", "Pipeline interno (slug)", "embajadores_nuevo"],
              ["odoo_perdido_field", "Campo 'perdido' en Odoo", "perdido"],
            ].map(([k, lbl, ph]) => (
              <label key={k} className="block">
                <span className="text-[11px] uppercase tracking-wide text-carbon-500">{lbl}</span>
                <input type={k === "odoo_password" ? "password" : "text"}
                  value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  placeholder={ph} data-testid={`odoo-${k}`}
                  className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm" />
              </label>
            ))}
            <label className="flex items-center gap-2 mt-2">
              <input type="checkbox" checked={!!form.preview_mode} data-testid="odoo-preview-toggle"
                onChange={(e) => setForm({ ...form, preview_mode: e.target.checked })} />
              <span className="text-sm">Modo preview (simula Odoo, no envía nada real)</span>
            </label>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button onClick={save} disabled={saving} data-testid="odoo-save-btn"
              className="bg-terracotta text-white text-sm px-4 py-2 rounded hover:bg-terracotta-hover disabled:opacity-50">
              {saving ? "Guardando…" : "Guardar"}
            </button>
            <button onClick={() => run("Conexión", () => api.post("/odoo/test-connection"))} disabled={!!busy}
              data-testid="odoo-test-conn-btn"
              className="text-sm px-4 py-2 rounded border border-carbon-200 hover:border-terracotta flex items-center gap-2">
              <Plugs size={15} /> Probar conexión
            </button>
            <button onClick={() => run("Lectura", () => api.post("/odoo/test-update"))} disabled={!!busy}
              className="text-sm px-4 py-2 rounded border border-carbon-200 hover:border-terracotta flex items-center gap-2">
              <ArrowsClockwise size={15} /> Probar lectura
            </button>
          </div>
        </Card>

        {/* Etapas → Odoo */}
        <Card className="p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="section-label">Etapas del pipeline → Odoo (crm.stage)</div>
            <div className="flex gap-2">
              <button onClick={async () => setStages((await run("Etapas", () => api.get("/odoo/stages")))) } disabled={!!busy}
                className="text-xs px-3 py-1.5 rounded border border-carbon-200 hover:border-terracotta flex items-center gap-1">
                <StackSimple size={13} /> Ver etapas
              </button>
              <button onClick={() => run("Sincronizar etapas", () => api.post("/odoo/sync-stages"))} disabled={!!busy}
                data-testid="odoo-sync-stages-btn"
                className="text-xs px-3 py-1.5 rounded bg-terracotta text-white hover:bg-terracotta-hover">
                Enviar/crear etapas en Odoo
              </button>
            </div>
          </div>
          {stages && (
            <div className="text-sm">
              {stages.found ? (
                <ol className="list-decimal ml-5 space-y-0.5">
                  {stages.stages.map((s) => <li key={s.stage_key}>{s.name} <span className="text-carbon-400 font-mono text-xs">({s.stage_key})</span></li>)}
                </ol>
              ) : <div className="text-signal">Pipeline «{stages.pipeline_slug}» no encontrado en esta base. Ajusta el slug arriba.</div>}
            </div>
          )}
        </Card>

        {/* Webhooks */}
        <Card className="p-5 mb-4">
          <div className="section-label mb-3">Webhooks para automatizaciones de Odoo</div>
          {tokenInfo && (
            <div className="space-y-3 text-sm">
              <Row label="Event token (Bearer)" value={tokenInfo.event_token} onCopy={() => copy(tokenInfo.event_token)} mono />
              <Row label="Endpoint genérico" value={tokenInfo.endpoints.generic} onCopy={() => copy(tokenInfo.endpoints.generic)} mono />
              {Object.entries(tokenInfo.endpoints.typed).map(([slug, url]) => (
                <Row key={slug} label={slug} value={url} onCopy={() => copy(url)} mono />
              ))}
              <div className="text-[11px] text-carbon-500">
                En Odoo, en cada automatización, envía POST a estas URLs con el header
                <code className="bg-page px-1 rounded"> Authorization: Bearer &lt;token&gt;</code> y un JSON con:
                <code className="bg-page px-1 rounded"> event, event_id, timestamp, odoo_lead_id, name, phone, stage</code>.
              </div>
            </div>
          )}
        </Card>

        {/* Inspección de campos */}
        <Card className="p-5">
          <div className="section-label mb-3">Inspeccionar campos de crm.lead</div>
          <div className="flex gap-2 mb-3">
            <input value={fieldQuery} onChange={(e) => setFieldQuery(e.target.value)}
              placeholder="filtrar (ej: x_, score, perdido)"
              className="flex-1 bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
            <button onClick={async () => setFields((await run("Campos", () => api.get(`/odoo/lead-fields?contains=${encodeURIComponent(fieldQuery)}`)))?.fields || {})}
              disabled={!!busy}
              className="text-sm px-4 py-2 rounded border border-carbon-200 hover:border-terracotta flex items-center gap-2">
              <MagnifyingGlass size={15} /> Inspeccionar
            </button>
          </div>
          {fields && (
            <div className="max-h-72 overflow-y-auto text-xs font-mono border border-carbon-200 rounded p-3 bg-page">
              {Object.entries(fields).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 py-0.5">
                  <span className="text-terracotta">{k}</span>
                  <span className="text-carbon-500">{v.string} · {v.type}</span>
                </div>
              ))}
              {Object.keys(fields).length === 0 && <div className="text-carbon-400">Sin resultados.</div>}
            </div>
          )}
        </Card>
      </PageBody>
    </>
  );
}

const Row = ({ label, value, onCopy, mono }) => (
  <div className="flex items-center justify-between gap-3 border border-carbon-200 rounded px-3 py-2 bg-page">
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-carbon-500">{label}</div>
      <div className={`truncate ${mono ? "font-mono text-xs" : ""}`} title={value}>{value}</div>
    </div>
    <button onClick={onCopy} className="text-carbon-400 hover:text-terracotta shrink-0" title="Copiar">
      <Copy size={14} />
    </button>
  </div>
);
